import { cn } from '@/lib/utils';
import { Button, Icon, Spinner } from '@pms-digital/design-system';
import { useRef, useState } from 'react';

export interface SetupImportFileTabProps {
  onFile: (file: File) => void;
  /** Name of the file being read by the server, while the upload runs. */
  readingFileName: string | null;
}

/** « Importer un fichier » tab: choose or drop a `.xlsx` (board 02). */
export function SetupImportFileTab({ onFile, readingFileName }: SetupImportFileTabProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const reading = readingFileName !== null;

  return (
    <div
      data-testid="setup-import-dropzone"
      className={cn(
        'flex min-h-[220px] flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed bg-gray-50 p-6 text-center text-gray-600',
        dragging ? 'border-bleu-nuit' : 'border-gray-300',
      )}
      onDragOver={(event) => {
        event.preventDefault();
        if (!reading) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        const file = event.dataTransfer.files[0];
        if (file && !reading) onFile(file);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        aria-label="Fichier Excel du setup"
        data-testid="setup-import-file-input"
        disabled={reading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Reset so picking the same file again after a refusal still fires.
          event.target.value = '';
          if (file) onFile(file);
        }}
      />
      {reading ? (
        <output className="flex items-center gap-2 text-gray-800">
          <Spinner size="xs" label="" />
          Lecture de {readingFileName}…
        </output>
      ) : (
        <>
          <Icon name="table_view" size={32} />
          <strong className="text-body text-gray-900">Déposez un fichier .xlsx ici</strong>
          <span className="text-caption text-gray-400 uppercase tracking-[0.06em]">ou</span>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => inputRef.current?.click()}
          >
            Choisir un fichier
          </Button>
          <span className="text-caption text-gray-500">5 Mo max</span>
        </>
      )}
    </div>
  );
}
